import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertFilled,
  ArrowRightOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  ReloadOutlined,
  RiseOutlined,
  ShopOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import MobileContextSelectors from "../../mobile/MobileContextSelectors";
import "./RestaurantOwnerCockpit.css";

const formatMoney = (value) =>
  `${new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)} F`;

const formatCount = (value) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Number(value) || 0
  );

const formatRefreshTime = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "maintenant";
  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDateLabel = (value) => {
  const date = value ? new Date(`${value}T12:00:00`) : new Date();
  if (Number.isNaN(date.getTime())) return "Aujourd'hui";
  const label = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const severityLabel = {
  critical: "Urgent",
  warning: "À traiter",
  info: "À suivre",
};

const healthIcon = (status) =>
  status === "healthy" ? <CheckCircleFilled /> : <AlertFilled />;

const SummaryCard = ({ label, value, detail, icon, tone = "default" }) => (
  <article className={`roc__summary-card roc__summary-card--${tone}`}>
    <div className="roc__summary-head">
      <span>{label}</span>
      <span className="roc__summary-icon">{icon}</span>
    </div>
    <strong>{value}</strong>
    <small>{detail}</small>
  </article>
);

const LoadingState = () => (
  <div className="roc__loading" aria-label="Chargement du pilotage">
    <div className="roc__skeleton roc__skeleton--hero" />
    <div className="roc__skeleton-grid">
      <div className="roc__skeleton" />
      <div className="roc__skeleton" />
      <div className="roc__skeleton" />
      <div className="roc__skeleton" />
    </div>
    <div className="roc__skeleton roc__skeleton--card" />
    <div className="roc__skeleton roc__skeleton--card" />
  </div>
);

export default function RestaurantOwnerCockpit({ onProfileIneligible }) {
  const { user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const navigate = useNavigate();
  const [cockpit, setCockpit] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const firstName = useMemo(() => {
    const source =
      user?.first_name || user?.prenom || user?.name || user?.username || "Dirigeant";
    return String(source).trim().split(/\s+/)[0];
  }, [user]);

  const loadCockpit = useCallback(
    async (signal) => {
      if (!selectedCompany) {
        setCockpit(null);
        setStatus("idle");
        setError("");
        return;
      }

      setStatus("loading");
      setError("");

      try {
        const { data } = await apiClient.get("/api/restaurant/owner-cockpit", {
          params: {
            company_id: selectedCompany,
            ...(selectedWarehouse ? { warehouse_id: selectedWarehouse } : {}),
          },
          signal,
        });

        if (signal?.aborted) return;
        setCockpit(data);
        setStatus("ready");
      } catch (requestError) {
        if (signal?.aborted || requestError?.code === "ERR_CANCELED") return;

        const responseStatus = requestError?.response?.status;
        const responseCode = requestError?.response?.data?.code;
        if (
          responseStatus === 403 &&
          responseCode === "RESTAURANT_PROFILE_REQUIRED" &&
          typeof onProfileIneligible === "function"
        ) {
          onProfileIneligible();
          return;
        }

        setCockpit(null);
        setStatus("error");
        setError(
          requestError?.response?.data?.error ||
            "Le pilotage est momentanément indisponible."
        );
      }
    },
    [onProfileIneligible, selectedCompany, selectedWarehouse]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadCockpit(controller.signal);
    return () => controller.abort();
  }, [loadCockpit]);

  const refresh = () => {
    const controller = new AbortController();
    loadCockpit(controller.signal);
  };

  if (!selectedCompany) {
    return (
      <div className="roc">
        <MobileContextSelectors />
        <section className="roc__empty roc__empty--context">
          <ShopOutlined />
          <h1>Sélectionnez une entreprise</h1>
          <p>Le cockpit dirigeant apparaîtra dès que le contexte sera choisi.</p>
        </section>
      </div>
    );
  }

  if (status === "loading" && !cockpit) {
    return (
      <div className="roc">
        <MobileContextSelectors />
        <LoadingState />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="roc">
        <MobileContextSelectors />
        <section className="roc__empty roc__empty--error">
          <AlertFilled />
          <h1>Pilotage indisponible</h1>
          <p>{error}</p>
          <button type="button" onClick={refresh}>
            <ReloadOutlined /> Réessayer
          </button>
        </section>
      </div>
    );
  }

  if (!cockpit) return null;

  const { meta, summary, priorities, priority_count: priorityCount, health } =
    cockpit;
  const hiddenPriorityCount = Math.max(
    0,
    Number(priorityCount || 0) - priorities.length
  );
  const attentionCount = health.filter((item) => item.status !== "healthy").length;
  const netCashTone = Number(summary.net_cash) < 0 ? "danger" : "positive";

  return (
    <div className="roc">
      <div className="roc__context">
        <MobileContextSelectors />
      </div>

      <section className="roc__hero">
        <div className="roc__hero-topline">
          <span>{formatDateLabel(meta.date)}</span>
          <button
            type="button"
            className="roc__refresh"
            onClick={refresh}
            disabled={status === "loading"}
            aria-label="Actualiser le cockpit"
          >
            <ReloadOutlined spin={status === "loading"} />
            {formatRefreshTime(meta.generated_at)}
          </button>
        </div>
        <h1>Voici l’essentiel, {firstName}</h1>
        <p>
          {priorityCount > 0
            ? `${priorityCount} point${priorityCount > 1 ? "s" : ""} demande${
                priorityCount > 1 ? "nt" : ""
              } votre attention.`
            : "Votre restaurant ne présente aucune urgence détectée."}
        </p>
        <div className="roc__scope">
          <ShopOutlined />
          <span>{meta.company?.name}</span>
          <i />
          <span>{meta.scope_label}</span>
        </div>
      </section>

      <section className="roc__summary" aria-label="Synthèse du jour">
        <SummaryCard
          label="Ventes"
          value={formatMoney(summary.total_sales)}
          detail={`${formatCount(summary.ticket_count)} ticket(s)`}
          icon={<RiseOutlined />}
          tone="sales"
        />
        <SummaryCard
          label="Encaissements"
          value={formatMoney(summary.payments_in)}
          detail={`${formatMoney(summary.due_sales)} à suivre`}
          icon={<WalletOutlined />}
          tone="cash-in"
        />
        <SummaryCard
          label="Sorties"
          value={formatMoney(summary.cash_out)}
          detail={`${formatMoney(summary.expense_payments_out)} payés sur les charges`}
          icon={<WalletOutlined />}
          tone="cash-out"
        />
        <SummaryCard
          label="Solde net"
          value={formatMoney(summary.net_cash)}
          detail="Encaissements moins sorties"
          icon={<WalletOutlined />}
          tone={netCashTone}
        />
      </section>

      <section className="roc__section" aria-labelledby="roc-priorities-title">
        <div className="roc__section-head">
          <div>
            <span className="roc__eyebrow">Décisions</span>
            <h2 id="roc-priorities-title">Vos priorités</h2>
          </div>
          {priorityCount > 0 && <b>{priorityCount}</b>}
        </div>

        {priorities.length > 0 ? (
          <div className="roc__priority-list">
            {priorities.map((priority, index) => (
              <article
                key={priority.id}
                className={`roc__priority roc__priority--${priority.severity}`}
              >
                <div className="roc__priority-heading">
                  <span className="roc__priority-rank">{index + 1}</span>
                  <div>
                    <span className="roc__priority-severity">
                      {severityLabel[priority.severity] || "À vérifier"}
                    </span>
                    <h3>{priority.title}</h3>
                  </div>
                </div>
                <p>{priority.message}</p>
                {priority.impact_label && (
                  <div className="roc__impact">
                    <ClockCircleOutlined />
                    <strong>{priority.impact_label}</strong>
                  </div>
                )}
                {priority.evidence?.length > 0 && (
                  <div className="roc__evidence">
                    <span>Pourquoi ELSA le signale</span>
                    {priority.evidence.map((item) => (
                      <small key={item}>{item}</small>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="roc__priority-action"
                  onClick={() => navigate(priority.action.path)}
                >
                  {priority.action.label}
                  <ArrowRightOutlined />
                </button>
              </article>
            ))}
            {hiddenPriorityCount > 0 && (
              <p className="roc__hidden-priorities">
                + {hiddenPriorityCount} autre(s) point(s) visible(s) dans les modules
                concernés.
              </p>
            )}
          </div>
        ) : (
          <div className="roc__all-clear">
            <CheckCircleFilled />
            <div>
              <strong>Aucune action urgente</strong>
              <span>Les contrôles disponibles sont au vert.</span>
            </div>
          </div>
        )}
      </section>

      <section className="roc__section" aria-labelledby="roc-health-title">
        <div className="roc__section-head">
          <div>
            <span className="roc__eyebrow">Contrôle</span>
            <h2 id="roc-health-title">Santé de l’activité</h2>
          </div>
          <span className={`roc__health-count ${attentionCount ? "is-warning" : ""}`}>
            {attentionCount ? `${attentionCount} à surveiller` : "Tout va bien"}
          </span>
        </div>

        <div className="roc__health-list">
          {health.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`roc__health-item roc__health-item--${item.status}`}
              onClick={() => navigate(item.path)}
            >
              <span className="roc__health-icon">{healthIcon(item.status)}</span>
              <span className="roc__health-copy">
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
              </span>
              <ArrowRightOutlined />
            </button>
          ))}
        </div>
      </section>

      <section className="roc__section roc__activity" aria-labelledby="roc-activity-title">
        <div className="roc__section-head">
          <div>
            <span className="roc__eyebrow">Terrain</span>
            <h2 id="roc-activity-title">Points suivis maintenant</h2>
          </div>
        </div>
        <div className="roc__activity-grid">
          <div>
            <strong>{formatCount(cockpit.activity.active_kitchen_tickets)}</strong>
            <span>tickets cuisine</span>
          </div>
          <div>
            <strong>{formatCount(cockpit.activity.open_orders_to_close)}</strong>
            <span>commandes à clôturer</span>
          </div>
          <div>
            <strong>{formatCount(cockpit.activity.pending_regularizations)}</strong>
            <span>régularisations</span>
          </div>
          <div>
            <strong>{formatCount(cockpit.activity.stock_alerts)}</strong>
            <span>alertes stock</span>
          </div>
        </div>
      </section>

      {cockpit.data_quality?.partial && (
        <section className="roc__quality">
          <AlertFilled />
          <div>
            <strong>Analyse partielle</strong>
            {cockpit.data_quality.notes.map((note) => (
              <span key={note}>{note}</span>
            ))}
          </div>
        </section>
      )}

      <button
        type="button"
        className="roc__analysis-link"
        onClick={() => navigate("/tableau-de-bord")}
      >
        Voir l’analyse détaillée
        <ArrowRightOutlined />
      </button>
    </div>
  );
}
